import { Building2Icon, CalendarDaysIcon, CheckIcon, ClockIcon, MapPinIcon, PhoneIcon, Plus, TrashIcon, VideoIcon } from 'assets/icons';
import ActivityTimeline from 'components/activity/ActivityTimeline';
import EmployeeAvatar from 'components/assign/EmployeeAvatar';
import Badge from 'components/badge/badge';
import Button from 'components/button';
import { cardIconButtonClass } from 'components/card/PanelCard';
import CommentComposer from 'components/comments/CommentComposer';
import CommentThread from 'components/comments/CommentThread';
import useCommentThread from 'components/comments/useCommentThread';
import IconTooltip from 'components/iconTooltip/IconTooltip';
import SlideOverBody from 'components/SlideOver/component/body';
import SlideOverFooter from 'components/SlideOver/component/footer';
import SlideOverHeader from 'components/SlideOver/component/header';
import useSlideOver from 'components/SlideOver/useSlideOver';
import AutosaveTextarea from 'components/styledInput/AutosaveTextarea';
import LineTabs from 'components/tabs/LineTabs';
import { EVENT_CATEGORIES, eventCategoryOf, eventCategoryText } from 'constants/eventCategories';
import { describeRecurrence, languageCode } from 'helper/recurrence';
import { addDays, clockLabel, dayLabel, fromIsoDay, isoDay } from 'helper/timeLabels';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import { useNotifyPlanningChanged } from 'hooks/usePlanningChanged';
import useText from 'hooks/useText';
import { Repeat } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { crmTaskErrorText } from 'helper/crmTaskErrors';
import crmApi from 'server/crm/crmApi';
import AppointmentForm from './AppointmentForm';
import NotDoneDialog, { notDoneReasonText } from './NotDoneDialog';
import ScopeDialog from './ScopeDialog';
import AppointmentGoogleDetails from './panel/AppointmentGoogleDetails';
import AppointmentResponseBar from './panel/AppointmentResponseBar';

// A real company of Jobbiz has a page; the demo's own do not.
const isRealId = (id) => /^[0-9a-f]{24}$/i.test(id ?? '');

const nameOf = (person) => [person?.firstname, person?.lastname].filter(Boolean).join(' ');

// The rule of an appointment's series in words; « Se répète » while the
// series of a Google event is not read yet.
const seriesRuleOf = (series, recurrenceText, language) => {
  if (!series) return recurrenceText('repeats');
  if (!series.rule) return recurrenceText('googleCustom');
  return describeRecurrence(series.rule, isoDay(new Date(series.startsAt)), languageCode(language));
};

// « Aujourd'hui, 9:00 – 10:00 », « Mar. 27 oct., Toute la journée », or over
// several days « Du lun. 26 oct. au mer. 28 oct. ». A whole day's endDay is
// the day after its last one.
const whenOf = (item, start, end, now, text) => {
  const first = item.allDay && item.startDay ? fromIsoDay(item.startDay) : start;
  const last = item.allDay && item.endDay ? addDays(fromIsoDay(item.endDay), -1) : end;
  if (isoDay(first) !== isoDay(last)) {
    return item.allDay
      ? text('fromTo', { from: dayLabel(first), to: dayLabel(last) })
      : text('fromTo', { from: `${dayLabel(first)}, ${clockLabel(start)}`, to: `${dayLabel(last)}, ${clockLabel(end)}` });
  }
  let day = dayLabel(first);
  if (isoDay(first) === isoDay(now)) day = text('today');
  else if (isoDay(first) === isoDay(addDays(now, 1))) day = text('tomorrow');
  else if (isoDay(first) === isoDay(addDays(now, -1))) day = text('yesterday');
  return `${day}, ${item.allDay ? text('allDay') : `${clockLabel(start)} – ${clockLabel(end)}`}`;
};

/**
 * The panel of an appointment (slide-over `get_appointment`, its id in
 * `objID`), as on the prototype: its category, when, whether it was held,
 * the person met, where, its report written in place; its history and its
 * comments. « Réalisé » or « Non réalisé » with its reason (NotDoneDialog), at
 * any hour; called while not over. Its owner
 * or its creator (access.canEdit) changes its status, its report, deletes it;
 * they edit its title, location, slot and description in place of the detail
 * (AppointmentForm) unless Google Calendar forbids it
 * (access.canEditDetails). An occurrence of a series reads its rule under its
 * slot, and its delete asks for its scope (ScopeDialog). Every change tells the
 * planning (usePlanningChanged).
 */
const AppointmentPanel = () => {
  const { objID, setObjID } = UseContext();
  const id = typeof objID === 'string' ? objID : null;
  const { text, language } = useText('appointment');
  const { call, me } = useCrmContext();
  const { toggleSlideOver } = useSlideOver();
  const navigate = useNavigate();
  const location = useLocation();
  const notify = useNotifyPlanningChanged();
  const { text: tasksText } = useText('tasks');
  const { text: planningText } = useText('planning');
  const { text: recurrenceText } = useText('recurrence');
  const [item, setItem] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [team, setTeam] = useState([]);
  const [tab, setTab] = useState('detail');
  const [editing, setEditing] = useState(false);
  const [deleteScopeOpen, setDeleteScopeOpen] = useState(false);
  const [notDoneOpen, setNotDoneOpen] = useState(false);

  const load = useCallback(() => call(crmApi.getAppointment, { id }).then(setItem, (error) => {
        setItem(null);
        setLoadError(crmTaskErrorText(error, tasksText) ?? '');
      }),
    [call, id, tasksText],
  );
  useEffect(() => {
    if (!id || !me) return;
    setItem(null);
    setLoadError('');
    setTab('detail');
    setEditing(false);
    setDeleteScopeOpen(false);
    setNotDoneOpen(false);
    load();
    call(crmApi.getTeam).then(setTeam, () => setTeam([]));
  }, [call, load, id, me]);

  const thread = useCommentThread({
    // The comments of a private appointment of someone else are refused: none loaded.
    key: item && !item.masked ? id : null,
    load: () => call(crmApi.listAppointmentComments, { id }),
    add: (body, mentions) => call(crmApi.addAppointmentComment, { id, body, mentions }),
    update: (commentId, body, mentions) => call(crmApi.updateAppointmentComment, { id, commentId, body, mentions }),
    remove: (commentId) => call(crmApi.deleteAppointmentComment, { id, commentId }),
    people: team,
  });

  const close = () => toggleSlideOver();
  if (!item) {
    return (
      <>
        <SlideOverHeader hide={close} closeLabel={text('close')} />
        <SlideOverBody>
          {loadError ? (
            <p role="alert" className="mt-16 text-center text-sm text-gray-600">
              {loadError}
            </p>
          ) : (
            <div className="mx-auto mt-16 h-6 w-6 animate-spin rounded-full border-2 border-yellow-500 border-t-transparent" />
          )}
        </SlideOverBody>
      </>
    );
  }

  const now = new Date();
  const start = new Date(item.startsAt);
  const end = new Date(item.endsAt);
  // A private appointment of someone else: its slot and its owner only.
  if (item.masked) {
    return (
      <>
        <SlideOverHeader hide={close} closeLabel={text('close')}>
          {text('busy')}
        </SlideOverHeader>
        <SlideOverBody className="flex flex-col">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge type="PRIMARY">
                <ClockIcon wh={14} color="currentColor" />
                {whenOf(item, start, end, now, text)}
              </Badge>
            </div>
            {item.owner && (
              <p className="flex items-center gap-2 text-sm text-gray-700">
                <span className="flex shrink-0 text-gray-400">
                  <CalendarDaysIcon wh={16} color="currentColor" />
                </span>
                {text('calendarOf', { name: nameOf(item.owner) })}
              </p>
            )}
          </div>
        </SlideOverBody>
      </>
    );
  }
  const company = item.company;
  const category = eventCategoryOf(item.category);
  const contact = item.contactName || company?.contactName || '';
  const role = contact && contact === company?.contactName ? company?.contactRole : '';
  const phone = company?.phone ?? '';
  const late = end < now;
  const act = async (run, message) => {
    try {
      await run();
      toast.success(message);
      notify();
      close();
    } catch (error) {
      const message = crmTaskErrorText(error, tasksText);
      if (message) toast.error(message);
    }
  };
  // The answer of a guest: the appointment is read again, its access and its guests with it.
  const respond = (response) =>
    call(crmApi.respondAppointment, { id: item.id, response }).then(
      () => {
        notify();
        return load();
      },
      (error) => {
        const message = crmTaskErrorText(error, tasksText);
        if (message) toast.error(message);
      },
    );
  const mark = (status, message) => act(() => call(crmApi.updateAppointment, { id: item.id, fields: { status } }), message);
  const markNotDone = (notDoneReason, notDoneDetail) =>
    act(
      () => call(crmApi.updateAppointment, { id: item.id, fields: { status: 'no_show', notDoneReason, notDoneDetail } }),
      text('notDoneSaved'),
    );
  // An appointment marked not held before reasons existed reads « Non réalisé » alone.
  const notDoneLabel = (reason, detail) => {
    const why = notDoneReasonText(reason, detail, text);
    return why ? text('statusNotDone', { reason: why }) : text('statusNoShow');
  };
  const saveReport = (outcome) =>
    call(crmApi.updateAppointment, { id: item.id, fields: { outcome } }).then(
      () => {
        toast.success(text('reportSaved'));
        notify();
        return true;
      },
      (error) => {
        const message = crmTaskErrorText(error, tasksText);
        if (message) toast.error(message);
        return false;
      },
    );
  const [firstName, ...rest] = contact.split(' ');
  const canEdit = item.access?.canEdit !== false;
  const canEditDetails = canEdit && item.access?.canEditDetails !== false;
  const owner = item.owner?.id && item.owner.id !== me?.id ? item.owner : null;
  // A step without an actor comes from the synchronisation with Google Calendar.
  const history = (item.history ?? []).map((event) => ({
    id: event.id,
    icon: event.kind === 'created' ? <Plus wh={15} color="currentColor" /> : <CheckIcon wh={15} color="currentColor" />,
    text: event.kind === 'no_show' ? notDoneLabel(event.notDoneReason, event.notDoneDetail) : text(`history_${event.kind}`),
    at: new Date(event.at),
    lines: [event.actor ? nameOf(event.actor) : text('googleCalendar')],
  }));

  let actions = null;
  if (!canEdit) actions = null;
  else if (item.status !== 'planned') {
    actions = (
      <Button color="SECONDARY" size="SM" onClick={() => mark('planned', text('reopened'))}>
        {text('reopen')}
      </Button>
    );
  } else {
    actions = (
      <>
        {phone && !late && (
          <Button color="SECONDARY" size="SM" onClick={() => window.location.assign(`tel:${phone.replace(/\s/g, '')}`)}>
            <PhoneIcon wh={16} color="currentColor" />
            {text('call')}
          </Button>
        )}
        <Button color="SECONDARY" size="SM" onClick={() => setNotDoneOpen(true)}>
          {text('notDone')}
        </Button>
        <Button color="PRIMARY" size="SM" onClick={() => mark('done', text('heldDone'))}>
          {text('held')}
        </Button>
      </>
    );
  }

  return (
    <>
      <SlideOverHeader hide={close} closeLabel={text('close')}>
        {(category === 'interne' ? item.title : company?.name || item.title) || planningText('untitled')}
      </SlideOverHeader>
      <div className="flex-shrink-0 px-4 md:px-6">
        <LineTabs
          activeTab={tab}
          onChange={setTab}
          tabs={[
            { id: 'detail', label: text('tabDetail') },
            { id: 'activity', label: text('tabActivity') },
            { id: 'comments', label: text('tabComments') },
          ]}
        />
      </div>
      <SlideOverBody className="flex flex-col">
        {tab === 'comments' && (
          <CommentThread
            comments={thread.comments}
            people={team}
            meId={me?.id}
            onEdit={thread.edit}
            onDelete={thread.removeComment}
            pendingIds={thread.pendingIds}
          />
        )}
        {tab === 'activity' && <ActivityTimeline items={history} />}
        {tab === 'detail' && editing && (
          <AppointmentForm
            mode="edit"
            item={item}
            calendarSynced={Boolean(item.calendarSynced)}
            onCancel={() => setEditing(false)}
            onSaved={(updated) => {
              setEditing(false);
              notify();
              // A change of the following events may answer another
              // occurrence (the first of a new series): the panel opens it.
              if (updated?.id && updated.id !== item.id && setObjID) {
                setObjID(updated.id);
                return;
              }
              // The demo answers without history nor access: keep the loaded ones.
              setItem((current) => ({ ...current, ...updated }));
            }}
          />
        )}
        {tab === 'detail' && !editing && (
          <div className="flex flex-col gap-4">
            {canEditDetails && (
              <div className="flex justify-end">
                <Button color="SECONDARY" size="SM" onClick={() => setEditing(true)}>
                  {text('edit')}
                </Button>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-lg px-3 py-1 text-xs font-medium ${EVENT_CATEGORIES[category].tag}`}>
                {eventCategoryText(language, category, 'label')}
              </span>
              <Badge type={late && item.status === 'planned' ? 'ALERT' : 'PRIMARY'}>
                <ClockIcon wh={14} color="currentColor" />
                {whenOf(item, start, end, now, text)}
              </Badge>
              {item.status === 'done' && <Badge type="VALID">{text('statusDone')}</Badge>}
              {item.status === 'no_show' && (
                <Badge type="ALERT">{notDoneLabel(item.notDoneReason, item.notDoneDetail)}</Badge>
              )}
            </div>
            {item.recurring && (
              <p className="flex items-center gap-2 text-sm text-gray-700">
                <span className="flex shrink-0 text-gray-400">
                  <Repeat size={16} aria-hidden="true" />
                </span>
                {seriesRuleOf(item.series, recurrenceText, language)}
              </p>
            )}
            {owner && (
              <p className="flex items-center gap-2 text-sm text-gray-700">
                <span className="flex shrink-0 text-gray-400">
                  <CalendarDaysIcon wh={16} color="currentColor" />
                </span>
                {text('calendarOf', { name: nameOf(owner) })}
              </p>
            )}
            {(contact || company) && (
              <div className="flex items-center gap-3 rounded-xl border border-gray-200 p-3">
                <EmployeeAvatar employee={{ firstname: firstName || company?.name, lastname: rest.join(' ') }} size={40} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900">{contact || company?.name}</p>
                  <p className="text-xs text-gray-600">{[role, phone].filter(Boolean).join(' · ')}</p>
                </div>
                {company && isRealId(company.id) && (
                  <Button color="SECONDARY" size="SM" onClick={() => navigate(`/company/${company.id}`)}>
                    <Building2Icon wh={16} color="currentColor" />
                    {text('record')}
                  </Button>
                )}
              </div>
            )}
            {(item.location || item.mode) && (
              <div className="flex flex-col gap-2 text-sm text-gray-700">
                {item.location && (
                  <p className="flex items-center gap-2">
                    <span className="flex shrink-0 text-gray-400">
                      <MapPinIcon wh={16} color="currentColor" />
                    </span>
                    {item.location}
                  </p>
                )}
                {item.mode && (
                  <p className="flex items-center gap-2">
                    <span className="flex shrink-0 text-gray-400">
                      {item.mode === 'video' ? <VideoIcon wh={16} color="currentColor" /> : <Building2Icon wh={16} color="currentColor" />}
                    </span>
                    {text(`mode_${item.mode}`)}
                  </p>
                )}
              </div>
            )}
            <AppointmentGoogleDetails item={item} />
            <AutosaveTextarea
              key={item.id}
              label={text('report')}
              placeholder={text('reportPlaceholder')}
              initial={item.outcome ?? ''}
              onSave={saveReport}
              isDisabled={!canEdit}
            />
            {item.description && (
              <div>
                <p className="text-sm font-medium text-gray-700">{text('note')}</p>
                <p className="mt-1 whitespace-pre-line text-sm text-gray-600">{item.description}</p>
              </div>
            )}
          </div>
        )}
      </SlideOverBody>
      {tab === 'comments' && (
        <div className="flex-shrink-0 border-t border-gray-100 px-6 py-4">
          <CommentComposer people={team} meId={me?.id} onSend={thread.send} />
        </div>
      )}
      {item.access?.canRespond && !(tab === 'detail' && editing) && (
        <div className="flex-shrink-0 border-t border-gray-100">
          <AppointmentResponseBar current={item.myResponse} onRespond={respond} />
        </div>
      )}
      {/* While editing, a status change or a delete would drop the form. */}
      {!(tab === 'detail' && editing) && (
      <div className="flex-shrink-0 border-t border-gray-100">
        <SlideOverFooter>
          <div className="mr-auto flex items-center gap-2">
            {!location.pathname.startsWith('/planning') && (
              <IconTooltip label={text('seeInPlanning')}>
                <button
                  type="button"
                  aria-label={text('seeInPlanning')}
                  onClick={() => {
                    close();
                    navigate(`/planning?date=${isoDay(start)}`);
                  }}
                  className={cardIconButtonClass}
                >
                  <CalendarDaysIcon wh={18} color="#374151" />
                </button>
              </IconTooltip>
            )}
            {canEdit && (
            <IconTooltip label={text(item.visit ? 'unplace' : 'delete')}>
              <button
                type="button"
                aria-label={text(item.visit ? 'unplace' : 'delete')}
                onClick={() =>
                  item.recurring && !item.visit
                    ? setDeleteScopeOpen(true)
                    : act(() => call(crmApi.removeAppointment, { id: item.id }), text(item.visit ? 'unplaced' : 'deleted'))
                }
                className={cardIconButtonClass}
              >
                <TrashIcon wh={18} color="#374151" />
              </button>
            </IconTooltip>
            )}
          </div>
          {actions}
        </SlideOverFooter>
      </div>
      )}
      <NotDoneDialog
        open={notDoneOpen}
        onCancel={() => setNotDoneOpen(false)}
        onConfirm={(reason, detail) => {
          setNotDoneOpen(false);
          markNotDone(reason, detail);
        }}
      />
      <ScopeDialog
        open={deleteScopeOpen}
        action="delete"
        onCancel={() => setDeleteScopeOpen(false)}
        onConfirm={(scope) => {
          setDeleteScopeOpen(false);
          // Every scope deletes the occurrence shown: act closes the panel.
          act(() => call(crmApi.removeAppointment, { id: item.id, scope }), text('deleted'));
        }}
      />
    </>
  );
};

export default AppointmentPanel;
